import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { unitPrice } from "../lib/pricing";
import { trackAddToCart } from "../lib/pixels";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const KEY = "mt_cart";
const PROMO_KEY = "mt_promo";

export function CartProvider({ children }) {
  const { user } = useAuth();
  const restored = useRef(false);  // panier serveur récupéré (évite d'écraser avant la restauration)
  const cleared = useRef(false);   // clear() explicite (ex. paiement réussi) : ignorer la restauration en vol
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  const [promo, setPromo] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROMO_KEY) || "null"); } catch { return null; }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    if (promo) localStorage.setItem(PROMO_KEY, JSON.stringify(promo));
    else localStorage.removeItem(PROMO_KEY);
  }, [promo]);

  // À la connexion : récupérer le panier serveur (multi-appareils) si le panier local est vide
  useEffect(() => {
    if (!user?.email) { restored.current = false; cleared.current = false; return; }
    let alive = true;
    api.get("/cart")
      .then((r) => {
        if (!alive || cleared.current) return;  // clear() pendant la requête (ex. retour de paiement)
        const serverItems = (r.data?.items || []).map((i) => ({ ...i, key: i.key || `${Date.now()}-${Math.random()}` }));
        setItems((local) => {
          if (local.length === 0) return serverItems.length ? serverItems : local;
          // Rabais appliqué par l'admin dans l'onglet Paniers (custom_price) :
          // le reporter sur l'article LOCAL correspondant — bogue « rabais invisible
          // dans le panier client » (le panier local écrasait le prix ajusté)
          return local.map((i) => {
            const s = serverItems.find((x) =>
              x.product_id === i.product_id && x.size_id === i.size_id
              && (x.finish_id || "") === (i.finish_id || "")
              && (x.inscription_type || "") === (i.inscription_type || ""));
            return s && typeof s.custom_price === "number" ? { ...i, custom_price: s.custom_price } : i;
          });
        });
      })
      .catch(() => {})
      .finally(() => { if (alive) restored.current = true; });
    return () => { alive = false; };
  }, [user?.email]);

  // Synchronisation serveur (rappel panier abandonné + multi-appareils) — debounce 800 ms
  useEffect(() => {
    if (!user?.email || !restored.current) return;
    const timer = setTimeout(() => { api.put("/cart", { items }).catch(() => {}); }, 800);
    return () => clearTimeout(timer);
  }, [items, user?.email]);

  const add = (item) => {
    setItems((prev) => [...prev, { ...item, key: `${Date.now()}-${Math.random()}` }]);
    try {
      trackAddToCart(item, unitPrice(item.pricing, item.size_id, item.qty, item.inscription_type, item.finish_id) * item.qty);
    } catch { /* mesure publicitaire facultative */ }
  };
  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const updateQty = (key, qty) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, Math.min(1000, qty)) } : i)));
  const clear = () => { cleared.current = true; setItems([]); setPromo(null); };

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return (
    <CartContext.Provider value={{ items, add, remove, updateQty, clear, count, promo, applyPromo: setPromo, clearPromo: () => setPromo(null) }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
